import React, { useEffect, useState } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";

// Componentes de diseño estándar
import ListHeader from "../../../components/listing/ListHeader";
import ListFooter from "../../../components/listing/ListFooter";
import DynamicTable from "../../../components/listing/DynamicTable"; // <--- IMPORTANTE

// Componentes locales
import CrearPrecioProductoEessFuturo from "./CrearPrecioProductoEessFuturo";
import EditarPrecioProductoEessFuturo from "./EditarPrecioProductoEessFuturo";
import EliminarPrecioProductoEessFuturo from "./EliminarPrecioProductoEessFuturo";
import FiltroPrecioProductoEessFuturo from "./FiltroPrecioProductoEessFuturo";

const DEFAULT_PAGE_SIZE = 30;

export default function ListarPrecioProductoEessFuturo() {
  // ============================
  //        LISTADO
  // ============================
  const [items, setItems] = useState([]);
  const [paginaActual, setPaginaActual] = useState(1);
  const [porPagina, setPorPagina] = useState(DEFAULT_PAGE_SIZE);
  const [totalBackend, setTotalBackend] = useState(0);
  const [cargando, setCargando] = useState(false);
  
  // Modales
  const [mostrarCrear, setMostrarCrear] = useState(false);
  const [mostrarEditar, setMostrarEditar] = useState(false);
  const [registroEditar, setRegistroEditar] = useState(null);
  const [mostrarEliminar, setMostrarEliminar] = useState(false);
  const [registroEliminar, setRegistroEliminar] = useState(null);

  // ============================
  //        FILTROS
  // ============================
  const [filtros, setFiltros] = useState({});
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  const limpiarSeleccion = () => {
    setFiltros({});
    setPaginaActual(1);
  };

  const handleAplicarFiltros = (nuevosFiltros) => {
    setFiltros(nuevosFiltros);
    setPaginaActual(1);
    setMostrarFiltros(false);
  };

  // ============================
  //        FETCH
  // ============================
  const fetchPreciosFuturos = async (
    pageIndex = paginaActual - 1,
    size = porPagina,
    filtrosAplicados = filtros,
  ) => {
    setCargando(true);
    try {
      const params = new URLSearchParams();
      params.append("page", pageIndex);
      params.append("size", size);
      params.append("sort", "id,desc");

      const { eessNombre, productoNombre, usuarioNombre, ...filtrosLimpios } = filtrosAplicados;
      
      Object.keys(filtrosLimpios).forEach((key) => {
        const value = filtrosLimpios[key];
        if (value !== null && value !== "" && value !== undefined) {
          params.append(key, value);
        }
      });

      const url = `${apiRoutes.precioProductoEessFuturo.list}?${params.toString()}`;
      const data = await appFetchJson(url);
      
      setItems(data?.items || []);
      setTotalBackend(data?.totalElements ?? 0);

    } catch (err) {
      console.error("Error al cargar precios futuros:", err);
      setItems([]);
      setTotalBackend(0);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    fetchPreciosFuturos(paginaActual - 1, porPagina, filtros);
  }, [paginaActual, porPagina, filtros]);

  // ============================
  //        ACCIONES
  // ============================
  const cambiarPagina = (num) => {
    setPaginaActual(num);
  };

  const handleEliminar = (row) => {
    setRegistroEliminar(row);
    setMostrarEliminar(true);
  };

  const handleEditar = (row) => {
    setRegistroEditar(row);
    setMostrarEditar(true);
  };

  // ====================================================================
  // DEFINICIÓN DE COLUMNAS (TOTAL 100% - 9 COLUMNAS)
  // ====================================================================
  const columns = [
    // 1. PRODUCTO (18%)
    { 
      header: "Producto", 
      type: "text", 
      width: "18%", 
      cellClass: "whitespace-normal break-words leading-tight font-medium text-gray-800",
      render: (row) => row.productoNombre || row.productoId || "-"
    },

    // 2. PRECIO VENTA (9%)
    { 
      header: "Valor Precio", 
      type: "center", 
      width: "9%", 
      render: (row) => row.precio ? Number(row.precio).toLocaleString('es-PY') : '-'
    },

    // 3. PRECIO COMPRA (9%)
    { 
      header: "Precio Compra", 
      type: "center", 
      width: "9%", 
      render: (row) => row.precio2 ? Number(row.precio2).toLocaleString('es-PY') : '-'
    },

    // 4. FECHA CARGA (9%)
    { 
      header: "Fecha Carga", 
      accessor: "fecha", 
      type: "center", 
      width: "9%" 
    },

    // 5. FECHA VIGENCIA (10%) - Importante, resaltada
    { 
      header: "Fecha Vigencia", 
      type: "center", 
      width: "10%",
      cellClass: "font-medium text-blue-700 text-center", // Azul para destacar que es futuro
      render: (row) => row.fechaVigenciaIni || "-"
    },

    // 6. EE.SS. (15%)
    { 
      header: "EE.SS.", 
      type: "text", 
      width: "15%",
      cellClass: "text-gray-600 whitespace-normal break-words leading-tight",
      render: (row) => row.eessNombre || row.eessId || "-"
    },

    // 7. USUARIO (10%)
    { 
      header: "Usuario", 
      type: "text", 
      width: "10%",
      cellClass: "text-gray-500 text-xs truncate", // Texto pequeño y cortado si es largo
      render: (row) => row.usuarioNombre || row.usuarioId || "-"
    },

    // 8. ESTADO (10%)
    { 
      header: "Estado", 
      type: "status", 
      width: "10%",
      render: (it) => {
        const desc = (it.estadoDescripcion || (it.estado === 12 ? "ACTIVO" : "INACTIVO")).toUpperCase();
        const esActivo = desc.includes('ACTIVO') && !desc.includes('INACTIVO');
        const textColor = esActivo ? 'text-green-600' : 'text-red-600';
        return <span className={`font-bold ${textColor}`}>{it.estadoDescripcion || desc}</span>;
      }
    },

    // 9. ACCIÓN (10%)
    {
      header: "Acción",
      type: "action", 
      width: "10%",
      render: (it) => (
        <div className="flex justify-center items-center gap-1">
          <button className="text-gray-500 hover:text-blue-600 p-1" onClick={() => handleEditar(it)} title="Editar">
            <FaEdit />
          </button>
          <button className="text-gray-500 hover:text-red-600 p-1" onClick={() => handleEliminar(it)} title="Eliminar">
            <FaTrash />
          </button>
        </div>
      )
    }
  ];

  const getRowBorderColor = (row) => {
    const desc = (row.estadoDescripcion || (row.estado === 12 ? "ACTIVO" : "INACTIVO")).toUpperCase();
    const esActivo = desc.includes('ACTIVO') && !desc.includes('INACTIVO');
    return esActivo ? 'border-green-600' : 'border-red-600';
  };

  // ============================
  //        RENDER
  // ============================
  return (
    <div className="page">

      <ListHeader
        title="Precios Futuros por EE.SS."
        onFilterClick={() => setMostrarFiltros(true)}
        showAdd={true}
        onAddClick={() => setMostrarCrear(true)}
        filters={filtros}
        onClearFilters={limpiarSeleccion}
        pageSize={porPagina}
        onPageSizeChange={(size) => {
          setPorPagina(size);
          setPaginaActual(1);
        }}
      />

      {/* DYNAMIC TABLE */}
      {cargando && <div className="mt-2 text-sm text-center text-gray-500">Cargando datos...</div>}
      
      {!cargando && (
        <DynamicTable 
          size="L" // 'L' (1210px) es necesario porque son 9 columnas
          data={items}
          columns={columns}
          getRowColor={getRowBorderColor}
        />
      )}

      <ListFooter
        page={paginaActual}
        pageSize={porPagina}
        total={totalBackend}
        onPageChange={cambiarPagina}
      />

      {/* MODALES */}
      {mostrarCrear && (
        <CrearPrecioProductoEessFuturo
          onClose={() => setMostrarCrear(false)}
          onCreated={() => {
            setPaginaActual(1);
            fetchPreciosFuturos(0, porPagina, filtros);
          }}
        />
      )}

      {mostrarEditar && registroEditar && (
        <EditarPrecioProductoEessFuturo
          record={registroEditar}
          onClose={() => {
            setMostrarEditar(false);
            setRegistroEditar(null);
          }}
          onUpdated={() => {
            fetchPreciosFuturos(paginaActual - 1, porPagina, filtros);
          }}
        />
      )}

      {mostrarEliminar && registroEliminar && (
        <EliminarPrecioProductoEessFuturo
          registro={registroEliminar}
          onClose={() => {
            setMostrarEliminar(false);
            setRegistroEliminar(null);
          }}
          onDeleted={() => {
            fetchPreciosFuturos(paginaActual - 1, porPagina, filtros);
          }}
        />
      )}

      {mostrarFiltros && (
        <FiltroPrecioProductoEessFuturo
          initialFilters={filtros}
          onSubmit={handleAplicarFiltros}
          onClose={() => setMostrarFiltros(false)}
        />
      )}
    </div>
  );
}